import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const Counter = ({ target, isPercent, shouldStart }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!shouldStart) return;

    let start = 0;
    const end = parseInt(target, 10);

    if (isNaN(end)) return;

    if (start === end) {
      setCount(end);
      return;
    }

    const duration = 1600;
    const incrementTime = 30;
    const step = Math.max(
      1,
      Math.ceil(end / (duration / incrementTime))
    );

    const timer = setInterval(() => {
      start += step;

      if (start >= end) {
        start = end;
        clearInterval(timer);
      }

      setCount(start);
    }, incrementTime);

    return () => clearInterval(timer);
  }, [target, shouldStart]);

  return (
    <p className="mb-1 text-2xl font-bold tabular-nums text-slate-900 sm:text-3xl">
      {count}
      {isPercent ? '%' : '+'}
    </p>
  );
};

const AboutUs = () => {
  const { t } = useTranslation();

  // Stats animation
  const statsRef = useRef(null);

  const statsInView = useInView(statsRef, {
    once: true,
    margin: '-100px',
  });

  // Dynamic typed text
  const [typedText, setTypedText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentServiceIndex, setCurrentServiceIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  // Get capabilities from translation
  const translatedCapabilities = t('hero.capabilities', {
    returnObjects: true,
  });

  // Make sure capabilities is always an array
  const capabilities = Array.isArray(translatedCapabilities)
    ? translatedCapabilities
    : [];

  // Blue-family gradients
  const gradients = [
    'from-blue-600 to-indigo-600',
    'from-sky-500 to-blue-600',
    'from-indigo-500 to-blue-500',
    'from-blue-500 to-cyan-500',
    'from-blue-600 to-sky-500',
    'from-indigo-600 to-blue-600',
  ];

  // Typing animation
  useEffect(() => {
    if (!capabilities.length) {
      setTypedText('');
      return;
    }

    const current =
      capabilities[currentServiceIndex] || capabilities[0];

    const typingSpeed = isDeleting ? 30 : 100;

    const timer = setTimeout(() => {
      if (!isDeleting) {
        const nextText = current.substring(0, currentIndex + 1);

        setTypedText(nextText);
        setCurrentIndex(currentIndex + 1);

        // Start deleting after the word is completely typed
        if (currentIndex + 1 >= current.length) {
          setTimeout(() => {
            setIsDeleting(true);
          }, 1500);
        }
      } else {
        const nextText = current.substring(0, currentIndex - 1);

        setTypedText(nextText);
        setCurrentIndex(currentIndex - 1);

        // Move to next capability
        if (currentIndex - 1 <= 0) {
          setIsDeleting(false);
          setCurrentIndex(0);

          setCurrentServiceIndex(
            (prev) => (prev + 1) % capabilities.length
          );
        }
      }
    }, typingSpeed);

    return () => clearTimeout(timer);
  }, [
    currentIndex,
    isDeleting,
    currentServiceIndex,
    capabilities,
  ]);

  return (
    <section className="relative overflow-hidden bg-bg-main py-14 sm:py-22 lg:py-18">

      {/* Decorative Line Art */}
      <svg
        className="pointer-events-none absolute inset-0 hidden h-full w-full sm:block"
        viewBox="0 0 1440 800"
        fill="none"
        preserveAspectRatio="xMidYMid slice"
      >
        <circle
          cx="120"
          cy="60"
          r="180"
          stroke="#D9A566"
          strokeOpacity="0.4"
          strokeWidth="1.5"
        />

        <circle
          cx="1050"
          cy="230"
          r="230"
          stroke="#D9A566"
          strokeOpacity="0.3"
          strokeWidth="1.5"
        />

        <path
          d="M340 -40 C 420 120, 260 260, 380 400"
          stroke="#D9A566"
          strokeOpacity="0.4"
          strokeWidth="1.5"
          fill="none"
        />

        <path
          d="M1250 60 C 1400 260, 1180 420, 1380 650"
          stroke="#D9A566"
          strokeOpacity="0.3"
          strokeWidth="1.5"
          fill="none"
        />
      </svg>

      <div className="relative mx-auto mt-10 max-w-4xl px-4 sm:px-8">

        {/* Main Heading */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: 0.6,
            ease: 'easeOut',
          }}
        >
          <h1 className="text-3xl font-bold leading-[1.15] tracking-tight text-text-primary dark:text-white sm:text-4xl lg:text-5xl lg:leading-[1.1]">
            {t('hero.title')}
          </h1>

          <p className="mt-4 max-w-2xl text-base leading-7 text-text-secondary dark:text-slate-400 sm:mt-6 sm:text-lg sm:leading-8">
            {t('hero.subtitle')}
          </p>
        </motion.div>

        {/* Sub Heading */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: 0.6,
            ease: 'easeOut',
            delay: 0.05,
          }}
          className="mt-6 sm:mt-8"
        >
          <h2 className="text-xl font-bold leading-[1.15] tracking-tight text-text-primary dark:text-white sm:text-2xl lg:text-3xl">
            {t('hero.engineeringTitle')}
          </h2>

          {/* Expertise / What We Do */}
          <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 sm:gap-8">

            {/* Expertise */}
            <div>
              <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-text-primary dark:text-white sm:text-base">
                {t('hero.expertise.title')}
              </h3>

              <ul className="space-y-2">
                {t('hero.expertise.items', {
                  returnObjects: true,
                }).map((item, index) => (
                  <li
                    key={index}
                    className="flex items-start gap-2 text-sm leading-6 text-text-secondary dark:text-blue-100 sm:text-base"
                  >
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500" />

                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* What We Do */}
            <div>
              <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-text-primary dark:text-white sm:text-base">
                {t('hero.whatWeDo.title')}
              </h3>

              <ul className="space-y-2">
                {t('hero.whatWeDo.items', {
                  returnObjects: true,
                }).map((item, index) => (
                  <li
                    key={index}
                    className="flex items-start gap-2 text-sm leading-6 text-text-secondary dark:text-blue-100 sm:text-base"
                  >
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-500" />

                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

          </div>
        </motion.div>

        {/* Stats */}
        <motion.div
          ref={statsRef}
          initial={{
            opacity: 0,
            y: 16,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          viewport={{
            once: true,
          }}
          transition={{
            duration: 0.6,
            ease: 'easeOut',
          }}
          className="mt-8 rounded-2xl border border-blue-100 bg-white/70 px-4 py-5 shadow-sm backdrop-blur-sm sm:mt-10 sm:px-6 sm:py-6"
        >
          <div className="grid grid-cols-1 gap-x-4 gap-y-6 text-center sm:grid-cols-3 sm:gap-x-6 sm:gap-y-8">

            {/* 100+ Years of Combined Experience */}
            <div>
              <Counter
                target={100}
                isPercent={false}
                shouldStart={statsInView}
              />

              <p className="text-xs text-slate-500 sm:text-sm">
                {t('hero.stats.experience')}
              </p>
            </div>

            {/* 20+ Technology Capabilities */}
            <div>
              <Counter
                target={20}
                isPercent={false}
                shouldStart={statsInView}
              />

              <p className="text-xs text-slate-500 sm:text-sm">
                {t('hero.stats.team')}
              </p>
            </div>

            {/* 97% Customer Satisfaction */}
            <div>
              <Counter
                target={97}
                isPercent={true}
                shouldStart={statsInView}
              />

              <p className="text-xs text-slate-500 sm:text-sm">
                {t('hero.stats.satisfaction')}
              </p>
            </div>

          </div>
        </motion.div>

        {/* Dynamic Capability Text */}
        {capabilities.length > 0 && (
          <motion.div
            initial={{
              opacity: 0,
              y: 12,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
            }}
            transition={{
              duration: 0.5,
              ease: 'easeOut',
              delay: 0.1,
            }}
            className="mt-6 text-center sm:mt-8"
          >
            <p className="text-lg font-semibold text-text-primary sm:text-xl">
              {t('hero.poweredBy')}{' '}

              <span
                className={`bg-gradient-to-r ${
                  gradients[currentServiceIndex % gradients.length]
                } bg-clip-text text-transparent`}
              >
                {typedText}
              </span>

              <span className="animate-pulse text-text-primary">
                |
              </span>
            </p>
          </motion.div>
        )}

      </div>
    </section>
  );
};

export default AboutUs;
